import React from 'react';
import { useTranslation } from 'react-i18next';
import { useCommonSelector } from '@wajeez-web/stores/common';
import { InputLabel } from '@wajeez-web/components/ui';
import { NumericFormType } from '../Types';
import {
  FieldValues,
  useController,
  UseControllerProps,
} from 'react-hook-form';

export interface FormNumberInputProps<TFormFields extends FieldValues>
  extends UseControllerProps<TFormFields> {
  fieldType: NumericFormType;
  disabled?: boolean;
  prefix?: React.ReactElement;
  suffix?: JSX.Element | null;
  shape?: 'circle' | 'squre';
  color?: string;
  bg?: 'primary' | 'secondary' | string;
  size?: 'sm' | 'med' | 'lg';
  widthClassName?: string;
}

const FormNumberInput = <TFormFields extends FieldValues>({
  fieldType,
  disabled,
  shape = 'squre',
  color = 'textPrimary',
  bg = 'white',
  prefix,
  suffix,
  widthClassName,
  ...props
}: FormNumberInputProps<TFormFields>) => {
  const { field, fieldState } = useController({
    ...props,
    rules: {
      required: fieldType.required ? 'Required' : undefined,
      min:
        fieldType.fieldOptions.min != undefined
          ? fieldType.fieldOptions.min
          : undefined,
      max:
        fieldType.fieldOptions.max != undefined
          ? fieldType.fieldOptions.max
          : undefined,
      ...props.rules,
    },
  });

  const { dir } = useCommonSelector((state) => state.common.settings);

  const { t } = useTranslation();

  const isError = fieldState.error; // && fieldState.isTouched;

  return (
    <div className={`${widthClassName}`}>
      {fieldType.label && (
        <InputLabel
          text={
            t(
              `inputs.labels.${fieldType.label}`,
              fieldType.label.toString() || ''
            ) as string
          }
          name={field.name}
          required={fieldType.required}
          hint={fieldType.span}
        />
      )}{' '}
      <div
        className={`relative w-full ${
          shape === 'circle' ? 'rounded-full' : 'rounded-md'
        } `}
      >
        {prefix && (
          <div
            className={`absolute inset-y-0 ltr:left-0 rtl:right-0  pl-3 flex items-center pointer-events-none`}
          >
            <span className="text-gray-500 sm:text-sm">{prefix}</span>
          </div>
        )}
        <input
          type="number"
          disabled={disabled}
          max={fieldType.fieldOptions.max}
          min={fieldType.fieldOptions.min}
          required={fieldType.required}
          {...field}
          value={field.value}
          onChange={(s) => {
            field.onChange(parseNumber(s.target.value));
          }}
          onBlur={field.onBlur}
          className={`w-full input focus:ring-ring-color shadow-sm focus:ring-4 cursor-pointer  focus:ring-opacity-10 focus:ring-offset-0  ${
            shape === 'circle' ? 'rounded-full' : 'rounded-md'
          } bg-${bg} ${
            isError
              ? `text-red-500`
              : `text-${color} dark:placeholder:text-dark-light`
          } focus:border-primary block  ${prefix && 'pl-7'}  sm:text-sm ${
            isError
              ? 'border-red-400 focus:ring-red-500 focus:border-red-400 '
              : 'border-gray-300'
          } `}
          placeholder={
            t(
              `inputs.placeholders.${fieldType.placeHolder}`,
              fieldType.placeHolder || ''
            ) as string
          }
        />
      </div>
    </div>
  );
};

// parse string to number and return undefined if not a number
export const parseNumber = (value: string) => {
  const parsedValue = parseFloat(value);
  return isNaN(parsedValue) ? undefined : parsedValue;
};

export default FormNumberInput;
